import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Inserisci email e password");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "tecnico" ? "/m/agenda" : "/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 md:w-1/2 md:px-16 lg:px-24">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl">
              <img src="/logo-sm.png" alt="Powercom" className="h-full w-full object-contain" />
            </span>
            <div className="leading-none">
              <span className="block text-2xl font-extrabold tracking-tight text-navy">FLUX</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                Powercom Workforce Management
              </span>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight text-navy">Accedi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Backoffice tecnici sul campo</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700"
              >
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
              Entra
            </Button>
          </form>
        </div>
      </div>

      {/* Brand panel */}
      <div className="relative hidden md:block md:w-1/2">
        <img
          src="https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg"
          alt="Rete fibra"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight">
            Gestione operativa degli interventi in tempo reale.
          </h2>
          <p className="mt-3 max-w-md text-white/70">
            Dashboard, pianificazione tecnici, ticket e listini — tutto in un unico backoffice.
          </p>
        </div>
      </div>
    </div>
  );
}
